import { useState, useEffect, useCallback } from "react"
import { ShiftsList } from "./ShiftsList"
import { supabase } from "@/integrations/supabase/client"
import type { Database } from "@/types/supabase-types"
import { Shift } from "@/types"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, Plus, Clock, Activity } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/auth"
import { ShiftDialogForm } from "./ShiftDialogForm"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
;

type ShiftRow = Database['ShiftSage']['Tables']['shifts']['Row']

export function ShiftSettings() {
  const [shifts, setShifts] = useState<ShiftRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedShift, setSelectedShift] = useState<ShiftRow | null>(null)
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false)
  const { user } = useAuth()

  // Load shifts on mount
  const fetchShifts = useCallback(async () => {
    if (!user?.companyId) {
      toast.error("Unable to fetch shifts: No company ID found")
      setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('company_id', user.companyId)
        .eq('is_del', false) // Only fetch non-deleted shifts
        .order('name')
      
      if (error) throw error
      
      // Convert to ShiftRow type
      const shiftsData: ShiftRow[] = (data || [])
      
      setShifts(shiftsData)
    } catch {
      toast.error("Failed to load shifts")
    } finally {
      setIsLoading(false)
    }
  }, [user]);

  useEffect(() => {
    fetchShifts();
  }, [user, fetchShifts]);

  // Event handler for saving a shift (new or existing)
  const handleSaveShift = (shift: Shift, isNew: boolean) => {
    // Convert Shift to ShiftRow
    const shiftRow: ShiftRow = {
      id: shift.id,
      company_id: shift.company_id || user?.companyId || '',
      name: shift.name,
      code: shift.code,
      valid_from: shift.valid_from,
      valid_to: shift.valid_to,
      day_time_deduction: shift.day_time_deduction,
      is_active: shift.is_active,
      is_del: shift.is_del || false,
      created_by: shift.created_by,
      created_at: shift.created_at,
      updated_by: shift.updated_by,
      updated_at: shift.updated_at
    }
    
    if (!isNew) {
      // ✅ NEW: Handle immutable versioning for shift edits
      // When editing a shift, the upsert_shift RPC creates a new shift and sets the old one to inactive
      // We need to find the old shift (the one being replaced) and update it to inactive
      // Then add the new shift to the list
      
      // Find the shift that was being edited (it should have the same name/code but different ID)
      const oldShift = shifts.find(s => 
        s.name === shiftRow.name && 
        s.code === shiftRow.code && 
        s.id !== shiftRow.id &&
        s.is_active // Only consider active shifts
      )
      
      if (oldShift) {
        // Set the old shift to inactive and add the new shift
        setShifts(prevShifts => 
          prevShifts.map(s => 
            s.id === oldShift.id 
              ? { ...s, is_active: false } // Set old shift to inactive
              : s
          ).concat([shiftRow]) // Add the new shift
        )
      } else {
        // Fallback: just add the new shift (in case we can't find the old one)
        setShifts(prevShifts => [...prevShifts, shiftRow])
      }
    } else {
      // Add new shift to local state
      setShifts([...shifts, shiftRow])
    }
    
    setIsShiftModalOpen(false)
  }
  
  // Event handler for removing a shift (mark as inactive)
  const handleRemoveShift = async (shiftId: string) => {
    try {
      // Mark as inactive by setting is_active to false
      const { error } = await supabase
        .from('shifts')
        .update({ is_active: false })
        .eq('id', shiftId)
      
      if (error) throw error
      
      // Update shift in local state
      setShifts(shifts.map(s => s.id === shiftId ? { ...s, is_active: false } : s))
      toast.success("Shift removed successfully")
    } catch {
      toast.error("Failed to remove shift")
    }
  }
  
  // Event handler for deleting a shift (mark as deleted)
  const handleDeleteShift = async (shiftId: string) => {
    try {
      // Mark as deleted by setting is_del to true
      const { error } = await supabase
        .from('shifts')
        .update({ is_del: true })
        .eq('id', shiftId)
      
      if (error) throw error
      
      // Remove shift from local state (since we filter by is_del = false)
      setShifts(shifts.filter(s => s.id !== shiftId))
      toast.success("Shift deleted successfully")
    } catch {
      toast.error("Failed to delete shift")
    }
  }

  const activeShifts = shifts.filter(s => s.is_active)
  const inactiveShifts = shifts.filter(s => !s.is_active)
  
  // Statistics cards are calculated from the local state (shifts array)
  // These values are derived from the database data fetched in fetchShifts()
  // - Total Shifts: shifts.length (total count from database)
  // - Active Shifts: shifts.filter(s => s.is_active).length (count of active shifts)
  // - Inactive Shifts: shifts.filter(s => !s.is_active).length (count of inactive shifts)
  
  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold text-foreground">Shift Management</CardTitle>
            </div>
          </div>
          <Button 
            onClick={() => {
              setSelectedShift(null)
              setIsShiftModalOpen(true)
            }}
            size="default"
            variant="secondary"
            className="px-4 h-10"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Shift
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading shifts...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Shifts</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{shifts.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50 p-4 rounded-lg border border-green-200/50 dark:border-green-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">Active Shifts</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">{activeShifts.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50 p-4 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Inactive Shifts</p>
                    <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{inactiveShifts.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Shifts List */}
            <div className="space-y-4">
              {shifts.length === 0 ? (
                <div className="py-8 text-center">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg mb-2">No shifts configured yet</p>
                  <p className="text-muted-foreground text-sm">Click 'Add Shift' to create your first work shift</p>
                </div>
              ) : (
                <ShiftsList
                  shifts={shifts}
                  onEditShift={(shift) => {
                    setSelectedShift(shift)
                    setIsShiftModalOpen(true)
                  }}
                  onRemoveShift={handleRemoveShift}
                  onDeleteShift={handleDeleteShift}
                />
              )}
            </div>
          </div>
        )}
      </CardContent>
      
      <Dialog open={isShiftModalOpen} onOpenChange={setIsShiftModalOpen}>
        <DialogContent className="sm:max-w-[600px] lg:max-w-[700px] max-h-[85vh] my-8">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl">
              {selectedShift ? "Edit Shift" : "Add New Shift"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            <ShiftDialogForm
              shift={selectedShift as Shift}
              onSave={handleSaveShift}
              onCancel={() => setIsShiftModalOpen(false)}
              existingShifts={shifts as Shift[]}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
